/**
 * PlanetGlobe — Three.js 3D sphere with drag-rotate, zoom, click-to-select,
 * orbital heatmap texture, and 3D GLB structure markers.
 *
 * v2: Loads GLB models for each structure type, positioned on the sphere
 * surface with proper orientation. Supports deep zoom for inspection.
 */

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  usePlanetPanelStore,
  type StructureWire,
  type MiningStatsWire,
} from './state/planetPanelStore';
import { bakeCloudCanvas } from './PlanetTerrainBaker';
import {
  buildPlanetHexMesh,
  type BuiltPlanetHexMesh,
  type ScannerZoneWire,
} from './PlanetHexMesh';

// ==================== GLB MODEL CACHE ====================

type StructureType = 'outpost' | 'mining_station' | 'relay' | 'scanner';

const MODEL_PATHS: Record<StructureType, string> = {
  outpost:        '/assets/3d/outpost.glb',
  mining_station: '/assets/3d/drill.glb',
  relay:          '/assets/3d/relay.glb',
  scanner:        '/assets/3d/scanner.glb',
};

// Scale each model so it looks right on the unit sphere.
const MODEL_SCALES: Record<StructureType, number> = {
  outpost:        0.0072,
  mining_station: 0.0048,
  relay:          0.0060,
  scanner:        0.0054,
};

/** Module-level cache: loaded once, reused across remounts. */
const modelCache: Partial<Record<StructureType, THREE.Group>> = {};
let modelsLoading = false;
let modelsReady = false;

function loadAllModels(): Promise<void> {
  if (modelsReady || modelsLoading) return Promise.resolve();
  modelsLoading = true;
  const loader = new GLTFLoader();
  const types = Object.keys(MODEL_PATHS) as StructureType[];
  return Promise.all(
    types.map(
      (type) =>
        new Promise<void>((resolve) => {
          loader.load(
            MODEL_PATHS[type],
            (gltf) => {
              modelCache[type] = gltf.scene;
              resolve();
            },
            undefined,
            (err) => {
              console.warn(`Failed to load ${type} model:`, err);
              resolve(); // graceful fallback — will use dot
            }
          );
        })
    )
  ).then(() => {
    modelsReady = true;
    modelsLoading = false;
  });
}

function cloneModel(type: StructureType): THREE.Group | null {
  const template = modelCache[type];
  if (!template) return null;
  return template.clone(true);
}

interface Props {
  onSelect(lat: number, lon: number, clickedStructureId: string | null): void;
}

export function PlanetGlobe({ onSelect }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // ============ Scene setup ============
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x070b14);

    const camera = new THREE.PerspectiveCamera(
      45,
      container.clientWidth / container.clientHeight,
      0.001,
      100
    );
    camera.position.set(0, 0, 4);
    // Smooth zoom target — we lerp the actual distance toward this.
    let zoomTarget = 4;

    // Center-on-outpost animation state.
    let centerSeq = -1;       // last consumed centerTarget.seq
    let centerRotX = 0;       // target spherePivot.rotation.x
    let centerRotY = 0;       // target spherePivot.rotation.y
    let centering = false;    // true while the animation is running

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    // ============ Starfield background ============
    {
      const starCount = 1200;
      const positions = new Float32Array(starCount * 3);
      for (let i = 0; i < starCount; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const r = 50;
        positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
        positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        positions[i * 3 + 2] = r * Math.cos(phi);
      }
      const starGeo = new THREE.BufferGeometry();
      starGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const starMat = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.15,
        sizeAttenuation: true,
      });
      scene.add(new THREE.Points(starGeo, starMat));
    }

    // ============ Lights ============
    const ambient = new THREE.AmbientLight(0xffffff, 0.45);
    scene.add(ambient);
    const sun = new THREE.DirectionalLight(0xffffff, 0.85);
    sun.position.set(5, 3, 5);
    scene.add(sun);

    // ============ Planet hex mesh ============
    // The hexsphere mesh replaces the texture-baked sphere. It's built lazily
    // on first valid planet snapshot (need profile + planetType). Until then
    // we ship a tiny empty mesh as a placeholder so the rest of the scene
    // (raycasting, marker pivot) has something to attach to.
    let sphere: THREE.Mesh = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    let hexBuilt: BuiltPlanetHexMesh | null = null;
    scene.add(sphere);

    // ============ Atmospheric rim glow ============
    const ATMOS_COLORS: Record<string, number> = {
      terran: 0x4488ff, ice: 0xaaccee, volcanic: 0xff6633, gas: 0xddbb88,
    };
    const atmosGeo = new THREE.SphereGeometry(1.03, 64, 32);
    const atmosMat = new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: new THREE.Color(0x4488ff) },
        power: { value: 4.0 },
      },
      vertexShader: `
        varying float vFresnel;
        void main() {
          vec3 worldNormal = normalize(mat3(modelMatrix) * normal);
          vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
          vec3 viewDir = normalize(cameraPosition - worldPos);
          vFresnel = 1.0 - dot(viewDir, worldNormal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        uniform float power;
        varying float vFresnel;
        void main() {
          float intensity = pow(vFresnel, power);
          gl_FragColor = vec4(glowColor, intensity * 0.5);
        }
      `,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
    });
    const atmosMesh = new THREE.Mesh(atmosGeo, atmosMat);
    atmosMesh.visible = false;

    // ============ Cloud layer ============
    const cloudGeo = new THREE.SphereGeometry(1.015, 64, 32);
    const cloudMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    });
    const cloudMesh = new THREE.Mesh(cloudGeo, cloudMat);
    cloudMesh.visible = false;

    // ============ Marker group ============
    const markerGroup = new THREE.Group();
    scene.add(markerGroup);

    // Selection marker — map-pin style (spike + sphere head).
    // Built pointing along +Y at unit scale; repositioned on click so +Y
    // aligns with the surface normal. Scaled each frame based on zoom.
    const selectionMarker = new THREE.Group();
    {
      const pinMat = new THREE.MeshBasicMaterial({ color: 0xffd54a });
      // Build at a "unit" size — we'll apply a uniform scale each frame.
      const pinHeight = 1.0;
      const spikeGeo = new THREE.ConeGeometry(0.05, pinHeight, 5);
      spikeGeo.translate(0, pinHeight / 2, 0);
      const spike = new THREE.Mesh(spikeGeo, pinMat);
      selectionMarker.add(spike);
      const headGeo = new THREE.SphereGeometry(0.18, 8, 6);
      const head = new THREE.Mesh(headGeo, pinMat);
      head.position.y = pinHeight + 0.14;
      selectionMarker.add(head);
    }
    selectionMarker.visible = false;
    scene.add(selectionMarker);

    // Structure selection ring — a fixed-size gold circle on the ground
    // around a clicked building. Built at unit scale, repositioned on click,
    // scaled each frame based on zoom (same logic as the pin).
    const structureHighlight = new THREE.Group();
    {
      const ringSegments = 64;
      const ringRadius = 1.0; // unit — scaled at render time
      const ringPoints: THREE.Vector3[] = [];
      for (let i = 0; i <= ringSegments; i++) {
        const a = (i / ringSegments) * Math.PI * 2;
        ringPoints.push(new THREE.Vector3(Math.cos(a) * ringRadius, 0, Math.sin(a) * ringRadius));
      }
      const outerGeo = new THREE.BufferGeometry().setFromPoints(ringPoints);
      const outerMat = new THREE.LineBasicMaterial({
        color: 0xffd54a,
        transparent: true,
        opacity: 0.9,
      });
      structureHighlight.add(new THREE.Line(outerGeo, outerMat));

      // Inner glow ring
      const innerPoints: THREE.Vector3[] = [];
      const innerR = 0.75;
      for (let i = 0; i <= ringSegments; i++) {
        const a = (i / ringSegments) * Math.PI * 2;
        innerPoints.push(new THREE.Vector3(Math.cos(a) * innerR, 0, Math.sin(a) * innerR));
      }
      const innerGeo = new THREE.BufferGeometry().setFromPoints(innerPoints);
      const innerMat = new THREE.LineBasicMaterial({
        color: 0xffd54a,
        transparent: true,
        opacity: 0.3,
      });
      structureHighlight.add(new THREE.Line(innerGeo, innerMat));
    }
    structureHighlight.visible = false;
    scene.add(structureHighlight);

    // Track what kind of selection we have.
    let selectionMode: 'none' | 'pin' | 'structure' = 'none';

    // ============ Render-on-demand flag ============
    // Set to true by any interaction or store change; cleared each frame.
    let needsRender = true;

    // ============ Pointer interaction ============
    let isDragging = false;
    let lastX = 0;
    let lastY = 0;
    let mouseDownX = 0;
    let mouseDownY = 0;
    let pointerDownTime = 0;
    // Sphere rotation state (we rotate the sphere, not the camera, so the
    // selection markers in `markerGroup` follow it consistently).
    const spherePivot = new THREE.Group();
    scene.remove(sphere);
    spherePivot.add(sphere);
    spherePivot.add(markerGroup);
    spherePivot.add(selectionMarker);
    spherePivot.add(structureHighlight);
    spherePivot.add(atmosMesh);
    spherePivot.add(cloudMesh);
    scene.add(spherePivot);

    const onPointerDown = (e: PointerEvent) => {
      isDragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      mouseDownX = e.clientX;
      mouseDownY = e.clientY;
      pointerDownTime = performance.now();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      // Adaptive drag speed: proportional to distance from surface.
      const dist = zoomTarget;
      const surfaceDist = Math.max(0.01, dist - 1.0);
      const dragSpeed = surfaceDist * 0.0017;
      spherePivot.rotation.y += dx * dragSpeed;
      spherePivot.rotation.x = Math.max(
        -Math.PI / 2 + 0.01,
        Math.min(Math.PI / 2 - 0.01, spherePivot.rotation.x + dy * dragSpeed)
      );
      needsRender = true;
    };
    const onPointerUp = (e: PointerEvent) => {
      isDragging = false;
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {}
      const dt = performance.now() - pointerDownTime;
      const moved = Math.hypot(e.clientX - mouseDownX, e.clientY - mouseDownY);
      if (dt < 250 && moved < 6) {
        // Left click = select, right click handled in contextmenu
        if (e.button === 0) handleClick(e);
      }
    };
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      // Right click = clear selection
      clearSelection();
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Adaptive scroll speed: smaller steps when close, bigger when far.
      const dist = camera.position.length();
      const scrollScale = 0.0003 + (dist - 1.0) * 0.0007;
      const factor = Math.exp(e.deltaY * scrollScale);
      // Min zoom: 1.4 = camera ~40% above unit sphere. Below this the camera
      // can dive inside a single hex prism — confusing UX given hex resolution.
      zoomTarget = Math.max(1.4, Math.min(12, zoomTarget * factor));
    };

    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();

    function clearSelection() {
      selectionMarker.visible = false;
      structureHighlight.visible = false;
      selectionMode = 'none';
      if (hexBuilt) hexBuilt.setSelectedHex(null);
      needsRender = true;
      usePlanetPanelStore.getState().setSelection(null);
    }

    /** Position the pre-built highlight ring on the sphere surface at (lat, lon). */
    function placeHighlightRing(lat: number, lon: number) {
      const v = latLonToUnit(lat, lon);
      structureHighlight.position.copy(v).multiplyScalar(1.002);
      // Orient: the ring is built in the XZ plane (Y=up). Align Y with surface normal.
      const up = new THREE.Vector3(0, 1, 0);
      const quat = new THREE.Quaternion().setFromUnitVectors(up, v.clone().normalize());
      structureHighlight.quaternion.copy(quat);
    }

    function handleClick(e: PointerEvent) {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObject(sphere, false);
      if (hits.length === 0) return;
      const hit = hits[0];

      // Resolve clicked hex from the triangle index → cellId lookup baked
      // into the mesh. Snap lat/lon to the hex centroid so the panel
      // (and downstream plant call) operate on the same hex the player saw.
      const faceToCell = sphere.userData.faceToCell as Uint16Array | undefined;
      let clickedHexId: number | null = null;
      if (faceToCell && typeof hit.faceIndex === 'number') {
        clickedHexId = faceToCell[hit.faceIndex] ?? null;
      }
      let lat: number;
      let lon: number;
      if (clickedHexId != null && hexBuilt) {
        const cell = hexBuilt.getCell(clickedHexId);
        lat = cell.lat;
        lon = cell.lon;
      } else {
        const local = sphere.worldToLocal(hit.point.clone());
        ({ lat, lon } = unitVectorToLatLon(local));
      }

      // Structure click: a hex is occupied if any structure sits on it.
      const state = usePlanetPanelStore.getState();
      let clickedStructureId: string | null = null;
      if (clickedHexId != null) {
        const occupant = state.structures.find((s) => s.hex_id === clickedHexId);
        if (occupant) clickedStructureId = occupant.id;
      }

      // Hex highlight does double duty for empty terrain AND occupied hexes.
      // The 3D model marker (GLB) on a structure already makes the hex
      // visually distinct; the gold outline confirms what you've selected.
      selectionMarker.visible = false;
      structureHighlight.visible = false;
      selectionMode = clickedStructureId ? 'structure' : 'pin';
      if (hexBuilt) hexBuilt.setSelectedHex(clickedHexId);

      needsRender = true;
      onSelectRef.current(lat, lon, clickedStructureId);
    }

    const dom = renderer.domElement;
    dom.style.touchAction = 'none';
    dom.addEventListener('pointerdown', onPointerDown);
    dom.addEventListener('pointermove', onPointerMove);
    dom.addEventListener('pointerup', onPointerUp);
    dom.addEventListener('pointercancel', onPointerUp);
    dom.addEventListener('wheel', onWheel, { passive: false });
    dom.addEventListener('contextmenu', onContextMenu);

    // ============ Hex mesh update (reactive to store) ============
    // No LOD textures — geometry is fixed at 642 cells. We only:
    //   - Build the mesh once when profile arrives.
    //   - Swap vertex-color attribute on terrain/resource toggle.
    //   - Re-color resource layer on depletion / scanner changes.
    const updateHexMesh = (() => {
      let lastSeed = -Infinity;
      let lastPlanetType = '';
      let lastLayer: 'terrain' | 'resource' | null = null;
      let lastResVer = -1;
      let lastScannerKey = '';
      let lastCloudSeed = -Infinity;
      return () => {
        const state = usePlanetPanelStore.getState();
        if (!state.planet) return;
        const p = state.planet;
        const seed = p.profile.profileSeed;
        const pt = p.planetType;
        const layer: 'terrain' | 'resource' = state.layers.noise ? 'resource' : 'terrain';
        const planetRadiusKm = p.radiusKm > 0 ? p.radiusKm : 6371 * Math.sqrt(p.profile.sizeMultiplier);

        // (Re)build mesh when seed/type changes.
        if (seed !== lastSeed || pt !== lastPlanetType) {
          lastSeed = seed;
          lastPlanetType = pt;

          // Dispose previous hex mesh if any.
          if (hexBuilt) {
            hexBuilt.dispose();
            hexBuilt = null;
          }
          // Dispose placeholder mesh contents (only on first build).
          sphere.geometry.dispose();
          if (Array.isArray(sphere.material)) {
            sphere.material.forEach((m) => m.dispose());
          } else {
            (sphere.material as THREE.Material).dispose();
          }

          hexBuilt = buildPlanetHexMesh(p.profile, pt, {
            planetRadiusKm,
            meshFrequency: p.meshFrequency ?? 8,
            initialLayer: layer,
            initialHexState: state.hexState,
            initialDiffs: state.hexState.length === 0 ? state.richnessDiffs : undefined,
            initialScannerZones: collectOwnScannerZones(state),
          });
          sphere.geometry = hexBuilt.mesh.geometry;
          sphere.material = hexBuilt.mesh.material;
          sphere.userData = hexBuilt.mesh.userData;

          lastLayer = layer;
          lastResVer = state.resourceVersion;
          lastScannerKey = scannerKeyOf(state);
        }

        if (!hexBuilt) return;

        // Layer toggle.
        if (layer !== lastLayer) {
          hexBuilt.setLayer(layer);
          lastLayer = layer;
        }

        // Depletion / enrichment changed.
        if (state.resourceVersion !== lastResVer) {
          if (state.hexState.length > 0) {
            hexBuilt.setHexState(state.hexState);
          } else {
            hexBuilt.setRichnessDiffs(state.richnessDiffs);
          }
          lastResVer = state.resourceVersion;
        }

        // Scanner zones changed.
        const sKey = scannerKeyOf(state);
        if (sKey !== lastScannerKey) {
          hexBuilt.setScannerZones(collectOwnScannerZones(state));
          lastScannerKey = sKey;
        }

        // Atmosphere visibility (unchanged from texture pipeline).
        atmosMesh.visible = pt in ATMOS_COLORS;
        if (atmosMesh.visible) {
          atmosMat.uniforms.glowColor.value.set(ATMOS_COLORS[pt]);
        }

        // Cloud texture (one-shot per seed).
        const hasClouds = pt === 'terran' || pt === 'ice' || pt === 'gas';
        if (hasClouds && seed !== lastCloudSeed) {
          lastCloudSeed = seed;
          const cc = bakeCloudCanvas(seed);
          const cloudTex = new THREE.CanvasTexture(cc);
          cloudTex.needsUpdate = true;
          const oldCloud = cloudMat.map;
          cloudMat.map = cloudTex;
          cloudMat.needsUpdate = true;
          if (oldCloud) oldCloud.dispose();
        }
        cloudMesh.visible = hasClouds && layer === 'terrain';
      };
    })();

    function scannerKeyOf(state: ReturnType<typeof usePlanetPanelStore.getState>): string {
      return state.structures
        .filter((s) => s.structure_type === 'scanner' && s.is_active && s.owner_player_id === state.selfPlayerId)
        .map((s) => `${s.id}:${s.lat}:${s.lon}`)
        .join('|');
    }

    function collectOwnScannerZones(
      state: ReturnType<typeof usePlanetPanelStore.getState>
    ): ScannerZoneWire[] {
      return state.structures
        .filter((s) => s.structure_type === 'scanner' && s.is_active && s.owner_player_id === state.selfPlayerId)
        .map((s) => ({ lat: s.lat, lon: s.lon, radiusKm: s.radius_km }));
    }

    // ============ Load GLB models ============
    loadAllModels();

    // ============ Marker rebuild (reactive to store) ============
    let lastStructureIds = '';
    let lastSelfId = '';
    let lastModelsReady = false;
    const rebuildMarkers = () => {
      const state = usePlanetPanelStore.getState();
      const { miningStats } = state;
      // Include health-relevant fields in the cache key so tints update on state changes.
      const key = state.structures.map((s) => {
        const ms = miningStats[s.id];
        const rBits = ms ? `${ms.organic.richness.toFixed(2)}:${ms.crystal.richness.toFixed(2)}` : '';
        return `${s.id}:${s.is_active ? 1 : 0}:${s.grace_period_started_at ?? ''}:${rBits}`;
      }).join(',');
      const selfKey = state.selfPlayerId ?? '';
      const modelsNowReady = modelsReady;
      if (
        key === lastStructureIds &&
        selfKey === lastSelfId &&
        modelsNowReady === lastModelsReady
      ) return;
      lastStructureIds = key;
      lastSelfId = selfKey;
      lastModelsReady = modelsNowReady;

      // Clear old markers (dispose geometries & materials to avoid leaks).
      disposeGroup(markerGroup);

      const planetRadiusKm = state.planet?.radiusKm ?? 6371;

      // --- Phase 1: Group own outpost + relay circles into operability zones ---
      const ownCoverageGroups = new Map<string, Array<{ lat: number; lon: number; angularRadius: number }>>();
      for (const s of state.structures) {
        if (s.owner_player_id !== state.selfPlayerId) continue;
        if (s.structure_type !== 'outpost' && s.structure_type !== 'relay') continue;
        if (!s.is_active) continue;
        const angularRadius = s.radius_km / planetRadiusKm;
        if (angularRadius <= 0.0001) continue;
        const groupId = s.structure_type === 'outpost' ? s.id : (s.parent_outpost_id ?? '');
        if (!groupId) continue;
        if (!ownCoverageGroups.has(groupId)) ownCoverageGroups.set(groupId, []);
        ownCoverageGroups.get(groupId)!.push({ lat: s.lat, lon: s.lon, angularRadius });
      }

      // --- Phase 2: Draw unified operability zone per outpost chain ---
      for (const [, circles] of ownCoverageGroups) {
        const arcs = traceUnionBoundary(circles);
        for (const arc of arcs) {
          const lineGeo = new THREE.BufferGeometry().setFromPoints(arc);
          const lineMat = new THREE.LineBasicMaterial({
            color: 0x4ea0ff,
            transparent: true,
            opacity: 0.7,
            linewidth: 1,
          });
          markerGroup.add(new THREE.Line(lineGeo, lineMat));
        }
        const fillGeo = buildUnionFillMesh(circles);
        if (fillGeo) {
          const fillMat = new THREE.MeshBasicMaterial({
            color: 0x4ea0ff,
            transparent: true,
            opacity: 0.08,
            side: THREE.DoubleSide,
            depthWrite: false,
          });
          markerGroup.add(new THREE.Mesh(fillGeo, fillMat));
        }
      }

      // --- Phase 3: Draw 3D models + individual circles for non-coverage structures ---
      for (const s of state.structures) {
        const isOwn = s.owner_player_id === state.selfPlayerId;
        const v = latLonToUnit(s.lat, s.lon);
        const normal = v.clone().normalize();

        // --- 3D model marker (or fallback dot) ---
        const model = cloneModel(s.structure_type as StructureType);
        if (model) {
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          model.children.forEach((child) => {
            child.position.x -= center.x;
            child.position.z -= center.z;
            child.position.y -= box.min.y;
          });
          const scale = MODEL_SCALES[s.structure_type as StructureType] ?? 0.01;
          model.scale.setScalar(scale);
          model.position.copy(v).multiplyScalar(1.002);
          const up = new THREE.Vector3(0, 1, 0);
          const quat = new THREE.Quaternion().setFromUnitVectors(up, normal);
          model.quaternion.copy(quat);
          tintModel(model, s, isOwn, miningStats);
          markerGroup.add(model);
        } else {
          const colour = colourForStructure(s, isOwn, miningStats);
          const health = isOwn && s.structure_type !== 'outpost'
            ? computeStructureHealth(s, miningStats) : 'green';
          const size = sizeForStructure(s);
          const dotGeo = new THREE.CircleGeometry(size, 16);
          const dotMat = new THREE.MeshBasicMaterial({
            color: colour,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: !isOwn ? (s.is_active ? 0.7 : 0.35) : (health === 'grey' ? 0.5 : 0.95),
          });
          const dotMesh = new THREE.Mesh(dotGeo, dotMat);
          dotMesh.position.copy(v).multiplyScalar(1.002);
          dotMesh.lookAt(v.clone().multiplyScalar(2));
          markerGroup.add(dotMesh);
        }

        // --- Individual coverage circles (skip own outpost/relay — already unified) ---
        const isUnifiedCoverage = isOwn && (s.structure_type === 'outpost' || s.structure_type === 'relay');
        if (!isUnifiedCoverage) {
          const angularRadius = s.radius_km / planetRadiusKm;
          if (angularRadius > 0.0001) {
            const circlePoints = traceCircleOnSphere(s.lat, s.lon, angularRadius);
            const lineGeo = new THREE.BufferGeometry().setFromPoints(circlePoints);
            const lineMat = new THREE.LineBasicMaterial({
              color: circleColourForStructure(s, isOwn, miningStats),
              transparent: true,
              opacity: isOwn ? 0.7 : 0.25,
              linewidth: 1,
            });
            markerGroup.add(new THREE.Line(lineGeo, lineMat));

            if (isOwn && s.is_active) {
              const fillPoints = traceCircleOnSphere(s.lat, s.lon, angularRadius, 48);
              const ctr = latLonToUnit(s.lat, s.lon).multiplyScalar(1.0015);
              const positions: number[] = [];
              for (let i = 0; i < fillPoints.length - 1; i++) {
                positions.push(ctr.x, ctr.y, ctr.z);
                positions.push(fillPoints[i].x, fillPoints[i].y, fillPoints[i].z);
                positions.push(fillPoints[i + 1].x, fillPoints[i + 1].y, fillPoints[i + 1].z);
              }
              const fillGeo = new THREE.BufferGeometry();
              fillGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
              const fillMat = new THREE.MeshBasicMaterial({
                color: circleColourForStructure(s, isOwn, miningStats),
                transparent: true,
                opacity: 0.08,
                side: THREE.DoubleSide,
                depthWrite: false,
              });
              markerGroup.add(new THREE.Mesh(fillGeo, fillMat));
            }
          }
        }
      }
    };

    // ============ Animation loop (render-on-demand) ============
    // Instead of rendering 60fps continuously, we only render when
    // something actually changes: zoom animating, drag, or store update.
    let raf = 0;

    // Mark scene dirty from any interaction.
    const requestRender = () => { needsRender = true; };

    // Subscribe to store changes that need a re-render.
    const unsubStore = usePlanetPanelStore.subscribe(requestRender);

    const tick = () => {
      raf = requestAnimationFrame(tick);

      // Smooth zoom lerp toward target.
      const currentDist = camera.position.length();
      const delta = zoomTarget - currentDist;
      const isZooming = Math.abs(delta) > 0.0005;
      if (isZooming) {
        needsRender = true;
        const newDist = currentDist + delta * 0.12;
        camera.position.setLength(newDist);
      }

      // Center-on-outpost: pick up new requests from the store.
      const ct = usePlanetPanelStore.getState().centerTarget;
      if (ct && ct.seq !== centerSeq) {
        centerSeq = ct.seq;
        centering = true;
        // Compute target Euler rotations to place (lat, lon) at camera front.
        const lonRad = (ct.lon * Math.PI) / 180;
        centerRotX = (ct.lat * Math.PI) / 180;
        centerRotY = -(Math.PI / 2 + lonRad);
        // Also zoom out a bit so the outpost area is clearly visible.
        zoomTarget = Math.min(zoomTarget, 3.0);
      }
      if (centering) {
        const lerpSpeed = 0.08;
        const dx = centerRotX - spherePivot.rotation.x;
        // Shortest-path wrap for Y rotation.
        let dy = centerRotY - spherePivot.rotation.y;
        dy = ((dy + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
        if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
          spherePivot.rotation.x = centerRotX;
          spherePivot.rotation.y = centerRotY;
          centering = false;
        } else {
          spherePivot.rotation.x += dx * lerpSpeed;
          spherePivot.rotation.y += dy * lerpSpeed;
        }
        needsRender = true;
      }

      // Skip all work if nothing changed.
      if (!needsRender) return;
      needsRender = false;

      const dist = camera.position.length();

      // Dynamic near plane.
      const surfaceDist = Math.max(0.001, dist - 1.0);
      camera.near = surfaceDist * 0.1;
      camera.updateProjectionMatrix();

      // Zoom-adaptive selection scale: bigger when far, smaller when close.
      const selScale = 0.005 + (dist - 1.0) * 0.003;
      if (selectionMarker.visible) {
        selectionMarker.scale.setScalar(selScale);
      }
      if (structureHighlight.visible) {
        structureHighlight.scale.setScalar(selScale);
      }

      // Hex mesh: cheap geometry updates (vertex color swap on layer toggle,
      // depletion repaint on resourceVersion bump). No LOD machinery.
      updateHexMesh();

      // Slow cloud rotation (continuous animation when visible).
      if (cloudMesh.visible) {
        cloudMesh.rotation.y += 0.0003;
        needsRender = true;
      }

      // Sync selection marker visibility with store (e.g. after dismantle clears selection).
      const sel = usePlanetPanelStore.getState().selection;
      if (!sel) {
        if (selectionMode !== 'none') {
          selectionMarker.visible = false;
          structureHighlight.visible = false;
          if (hexBuilt) hexBuilt.setSelectedHex(null);
          selectionMode = 'none';
        }
      } else if (sel.clickedStructureId && selectionMode === 'structure') {
        // If the selected structure was removed, the store clears selection —
        // handled above. But if it still exists, keep the highlight.
      }

      rebuildMarkers();
      renderer.render(scene, camera);
    };
    tick();

    // ============ Resize ============
    const onResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      needsRender = true;
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      unsubStore();
      window.removeEventListener('resize', onResize);
      dom.removeEventListener('pointerdown', onPointerDown);
      dom.removeEventListener('pointermove', onPointerMove);
      dom.removeEventListener('pointerup', onPointerUp);
      dom.removeEventListener('pointercancel', onPointerUp);
      dom.removeEventListener('wheel', onWheel);
      dom.removeEventListener('contextmenu', onContextMenu);
      container.removeChild(renderer.domElement);
      renderer.dispose();
      // Hex mesh owns sphere.geometry + sphere.material once built.
      if (hexBuilt) {
        hexBuilt.dispose();
      } else {
        sphere.geometry.dispose();
        if (Array.isArray(sphere.material)) {
          sphere.material.forEach((m) => m.dispose());
        } else {
          (sphere.material as THREE.Material).dispose();
        }
      }
      selectionMarker.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
      structureHighlight.traverse((child) => {
        if (child instanceof THREE.Line) {
          child.geometry?.dispose();
          if (child.material) (child.material as THREE.Material).dispose();
        }
      });
      atmosGeo.dispose();
      atmosMat.dispose();
      cloudGeo.dispose();
      cloudMat.dispose();
      if (cloudMat.map) cloudMat.map.dispose();
    };
  }, []);

  return <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: 0 }} />;
}

// ==================== HELPERS ====================

function latLonToUnit(lat: number, lon: number): THREE.Vector3 {
  const phi = (lat * Math.PI) / 180;
  const lambda = (lon * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  return new THREE.Vector3(
    cosPhi * Math.cos(lambda),
    Math.sin(phi),
    -cosPhi * Math.sin(lambda)
  );
}

function unitVectorToLatLon(v: THREE.Vector3): { lat: number; lon: number } {
  const n = v.clone().normalize();
  const lat = (Math.asin(n.y) * 180) / Math.PI;
  const lon = (Math.atan2(-n.z, n.x) * 180) / Math.PI;
  return { lat, lon };
}

function greatCircleKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  radiusKm: number
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radiusKm * c;
}

/**
 * Trace a circle on the unit sphere at `angularRadius` (radians) around
 * a centre (lat, lon in degrees). Returns an array of THREE.Vector3
 * positioned slightly above the surface (r = 1.003).
 */
function traceCircleOnSphere(
  centreLat: number,
  centreLon: number,
  angularRadius: number,
  segments: number = 64
): THREE.Vector3[] {
  const points: THREE.Vector3[] = [];
  const latR = (centreLat * Math.PI) / 180;
  const lonR = (centreLon * Math.PI) / 180;
  const sinLat = Math.sin(latR);
  const cosLat = Math.cos(latR);
  const sinAng = Math.sin(angularRadius);
  const cosAng = Math.cos(angularRadius);

  for (let i = 0; i <= segments; i++) {
    const bearing = (i / segments) * 2 * Math.PI;
    const sinB = Math.sin(bearing);
    const cosB = Math.cos(bearing);

    const lat2 = Math.asin(sinLat * cosAng + cosLat * sinAng * cosB);
    const lon2 =
      lonR +
      Math.atan2(sinB * sinAng * cosLat, cosAng - sinLat * Math.sin(lat2));

    const cosPhi = Math.cos(lat2);
    const r = 1.003; // slightly above surface
    points.push(
      new THREE.Vector3(
        r * cosPhi * Math.cos(lon2),
        r * Math.sin(lat2),
        r * -cosPhi * Math.sin(lon2)
      )
    );
  }
  return points;
}

/**
 * Build the outer boundary of a union of circles on the unit sphere.
 * For each circle, traces its perimeter and clips segments that fall
 * inside any other circle — the remaining arcs form the union outline.
 */
function traceUnionBoundary(
  circles: Array<{ lat: number; lon: number; angularRadius: number }>,
  segments: number = 96,
  surfaceR: number = 1.003
): THREE.Vector3[][] {
  if (circles.length === 0) return [];
  if (circles.length === 1) {
    return [traceCircleOnSphere(circles[0].lat, circles[0].lon, circles[0].angularRadius, segments)];
  }

  const arcs: THREE.Vector3[][] = [];

  for (let ci = 0; ci < circles.length; ci++) {
    const c = circles[ci];
    let arc: THREE.Vector3[] = [];
    const latR = (c.lat * Math.PI) / 180;
    const lonR = (c.lon * Math.PI) / 180;
    const sinLat = Math.sin(latR);
    const cosLat = Math.cos(latR);
    const sinAng = Math.sin(c.angularRadius);
    const cosAng = Math.cos(c.angularRadius);

    for (let i = 0; i <= segments; i++) {
      const bearing = (i / segments) * 2 * Math.PI;
      const sinB = Math.sin(bearing);
      const cosB = Math.cos(bearing);

      const ptLat = Math.asin(sinLat * cosAng + cosLat * sinAng * cosB);
      const ptLon =
        lonR +
        Math.atan2(sinB * sinAng * cosLat, cosAng - sinLat * Math.sin(ptLat));

      // Check if this boundary point is inside any OTHER circle.
      let insideOther = false;
      for (let oj = 0; oj < circles.length; oj++) {
        if (oj === ci) continue;
        const oLatR = (circles[oj].lat * Math.PI) / 180;
        const oLonR = (circles[oj].lon * Math.PI) / 180;
        const dLat = ptLat - oLatR;
        const dLon = ptLon - oLonR;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(ptLat) * Math.cos(oLatR) * Math.sin(dLon / 2) ** 2;
        const angDist = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        // Small shrink factor avoids z-fighting at intersection seams.
        if (angDist < circles[oj].angularRadius * 0.995) {
          insideOther = true;
          break;
        }
      }

      if (!insideOther) {
        const cosPhi = Math.cos(ptLat);
        arc.push(
          new THREE.Vector3(
            surfaceR * cosPhi * Math.cos(ptLon),
            surfaceR * Math.sin(ptLat),
            surfaceR * -cosPhi * Math.sin(ptLon)
          )
        );
      } else {
        if (arc.length > 1) arcs.push(arc);
        arc = [];
      }
    }
    if (arc.length > 1) arcs.push(arc);
  }

  return arcs;
}

/**
 * Build a filled mesh for the union of circles on the unit sphere
 * using grid-based sampling. Each cell whose centre falls inside
 * any circle becomes a quad (two triangles) on the sphere surface.
 */
function buildUnionFillMesh(
  circles: Array<{ lat: number; lon: number; angularRadius: number }>,
  gridResDeg: number = 0.5,
  surfaceR: number = 1.0015
): THREE.BufferGeometry | null {
  if (circles.length === 0) return null;

  // Bounding box in degrees.
  let minLat = 90;
  let maxLat = -90;
  let minLon = 180;
  let maxLon = -180;
  for (const c of circles) {
    const padDeg = (c.angularRadius * 180) / Math.PI + gridResDeg;
    minLat = Math.min(minLat, c.lat - padDeg);
    maxLat = Math.max(maxLat, c.lat + padDeg);
    minLon = Math.min(minLon, c.lon - padDeg);
    maxLon = Math.max(maxLon, c.lon + padDeg);
  }
  minLat = Math.max(-89, minLat);
  maxLat = Math.min(89, maxLat);

  const positions: number[] = [];
  const latSteps = Math.ceil((maxLat - minLat) / gridResDeg);
  const lonSteps = Math.ceil((maxLon - minLon) / gridResDeg);

  for (let li = 0; li < latSteps; li++) {
    for (let lj = 0; lj < lonSteps; lj++) {
      const lat0 = minLat + li * gridResDeg;
      const lon0 = minLon + lj * gridResDeg;
      const cLat = lat0 + gridResDeg / 2;
      const cLon = lon0 + gridResDeg / 2;
      const cLatR = (cLat * Math.PI) / 180;
      const cLonR = (cLon * Math.PI) / 180;

      let inside = false;
      for (const c of circles) {
        const oLatR = (c.lat * Math.PI) / 180;
        const oLonR = (c.lon * Math.PI) / 180;
        const dLat = cLatR - oLatR;
        const dLon = cLonR - oLonR;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(cLatR) * Math.cos(oLatR) * Math.sin(dLon / 2) ** 2;
        const angDist = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        if (angDist <= c.angularRadius) {
          inside = true;
          break;
        }
      }

      if (inside) {
        const lat1 = lat0 + gridResDeg;
        const lon1 = lon0 + gridResDeg;
        const p00 = latLonToUnit(lat0, lon0).multiplyScalar(surfaceR);
        const p10 = latLonToUnit(lat1, lon0).multiplyScalar(surfaceR);
        const p01 = latLonToUnit(lat0, lon1).multiplyScalar(surfaceR);
        const p11 = latLonToUnit(lat1, lon1).multiplyScalar(surfaceR);
        positions.push(
          p00.x, p00.y, p00.z,
          p10.x, p10.y, p10.z,
          p01.x, p01.y, p01.z,
          p01.x, p01.y, p01.z,
          p10.x, p10.y, p10.z,
          p11.x, p11.y, p11.z
        );
      }
    }
  }

  if (positions.length === 0) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  return geo;
}

// ==================== STRUCTURE HEALTH STATUS ====================

/** Traffic-light health for own structures. Other players always get a muted tint. */
type HealthStatus = 'green' | 'orange' | 'grey';

/** Low-richness threshold: below this a mining station is "about to deplete". */
const LOW_RICHNESS_THRESHOLD = 0.10;

/**
 * Compute the health status of a structure based on its operational state.
 *
 * Mining stations:
 *   green  — active, upkeep paid, richness healthy
 *   orange — active but grace period started OR richness low (< 10%)
 *   grey   — not active (depleted / disabled)
 *
 * Relay / Scanner (no mining output):
 *   green  — active, upkeep paid
 *   orange — grace period started (upkeep overdue)
 *
 * Outpost: always returns 'green' (rendered as blue via its own palette).
 */
function computeStructureHealth(
  s: StructureWire,
  miningStats: Record<string, MiningStatsWire>,
): HealthStatus {
  // Outpost is always "healthy" — its colour is handled separately.
  if (s.structure_type === 'outpost') return 'green';

  const inGrace = s.grace_period_started_at != null;

  if (s.structure_type === 'mining_station') {
    if (!s.is_active) return 'grey';
    if (inGrace) return 'orange';
    // Check richness — if either resource channel is very low, warn.
    const stats = miningStats[s.id];
    if (stats) {
      const orgLow = stats.organic.richness < LOW_RICHNESS_THRESHOLD;
      const cryLow = stats.crystal.richness < LOW_RICHNESS_THRESHOLD;
      if (orgLow && cryLow) return 'orange';
    }
    return 'green';
  }

  // Relay / Scanner — no mining output, just upkeep status.
  if (inGrace) return 'orange';
  return 'green';
}

// Health → colour mapping for own structures.
const HEALTH_COLOUR_GREEN  = 0x3ddc84; // vivid green
const HEALTH_COLOUR_ORANGE = 0xff8c00; // dark orange
const HEALTH_COLOUR_GREY   = 0x7a7a7a; // neutral grey
const OUTPOST_COLOUR       = 0x4ea0ff; // bright blue (unchanged)

function healthToColour(health: HealthStatus): number {
  switch (health) {
    case 'green':  return HEALTH_COLOUR_GREEN;
    case 'orange': return HEALTH_COLOUR_ORANGE;
    case 'grey':   return HEALTH_COLOUR_GREY;
  }
}

// Muted palette for other-player structures (unchanged from before).
const STRUCTURE_COLOUR_OTHER: Record<StructureType, number> = {
  outpost:        0x4a6478,
  mining_station: 0x7a614a,
  relay:          0x4a7878,
  scanner:        0x6a4a78,
};

function circleColourForStructure(
  s: StructureWire,
  isOwn: boolean,
  miningStats: Record<string, MiningStatsWire>,
): number {
  if (!isOwn) return 0x3a4a5a;
  if (s.structure_type === 'outpost') return OUTPOST_COLOUR;
  return healthToColour(computeStructureHealth(s, miningStats));
}

function colourForStructure(
  s: StructureWire,
  isOwn: boolean,
  miningStats: Record<string, MiningStatsWire>,
): number {
  if (!isOwn) return STRUCTURE_COLOUR_OTHER[s.structure_type as StructureType];
  if (s.structure_type === 'outpost') return OUTPOST_COLOUR;
  return healthToColour(computeStructureHealth(s, miningStats));
}

function sizeForStructure(s: StructureWire): number {
  switch (s.structure_type) {
    case 'outpost':         return 0.025;
    case 'mining_station':  return 0.012;
    case 'relay':           return 0.018;
    case 'scanner':         return 0.020;
  }
}

// ==================== MODEL HELPERS ====================

/** Apply health-based colour tint + opacity to a cloned model. */
function tintModel(
  model: THREE.Group,
  s: StructureWire,
  isOwn: boolean,
  miningStats: Record<string, MiningStatsWire>,
): void {
  const type = s.structure_type as StructureType;
  let tintHex: number;
  let opacity: number;
  let emissiveIntensity: number;

  if (!isOwn) {
    tintHex = STRUCTURE_COLOUR_OTHER[type];
    opacity = s.is_active ? 0.7 : 0.35;
    emissiveIntensity = 0.05;
  } else if (type === 'outpost') {
    tintHex = OUTPOST_COLOUR;
    opacity = 1.0;
    emissiveIntensity = 0.3;
  } else {
    const health = computeStructureHealth(s, miningStats);
    tintHex = healthToColour(health);
    opacity = health === 'grey' ? 0.5 : 1.0;
    emissiveIntensity = health === 'grey' ? 0.05 : 0.3;
  }

  const tint = new THREE.Color(tintHex);

  model.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const origMat = child.material as THREE.MeshStandardMaterial;
      const mat = origMat.clone();
      if (mat.color) {
        mat.color.lerp(tint, 0.6);
      }
      if (mat.emissive) {
        mat.emissive.copy(tint);
        mat.emissiveIntensity = emissiveIntensity;
      }
      mat.transparent = opacity < 1.0;
      mat.opacity = opacity;
      child.material = mat;
    }
  });
}

/** Recursively dispose all geometries and materials in a group, then clear children. */
function disposeGroup(group: THREE.Group): void {
  while (group.children.length > 0) {
    const child = group.children[0];
    group.remove(child);
    if (child instanceof THREE.Group) {
      disposeGroup(child);
    }
    if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
      child.geometry?.dispose();
      const mat = child.material;
      if (Array.isArray(mat)) {
        mat.forEach((m) => m.dispose());
      } else if (mat) {
        (mat as THREE.Material).dispose();
      }
    }
  }
}

